import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from "react";
import apiClient from "../utils/axios";

interface User {
  id?: number;
  email: string;
  first_name?: string;
  last_name?: string;
  date_joined?: string;
  is_staff?: boolean;
  is_active?: boolean;
  is_superuser?: boolean;
  lang?: string;
  can_receive_emails?: boolean;
  company?: number;
  two_factor_enabled?: boolean;
  onboarding_completed_at?: string | null;
  tos_accepted_at?: string | null;
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  login: (email: string, password: string) => Promise<{requires2FA?: boolean, userId?: number, needsOnboarding?: boolean}>;
  verify2FALogin: (otp: string, userId: number) => Promise<void>;
  setup2FA: () => Promise<{qrCodeUrl: string, secretKey: string}>;
  verify2FASetup: (otp: string) => Promise<boolean>;
  disable2FA: () => Promise<boolean>;
  logout: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  resetPassword: (token: string, password1: string, password2: string) => Promise<void>;
  validateResetToken: (token: string) => Promise<boolean>;
  acceptTerms: () => Promise<void>;
  completeOnboarding: () => Promise<void>;
}

const AuthProviderContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        console.log("Checking authentication...");
        const response = await apiClient.get('/auth/me/');
        console.log("Response from /auth/me/:", response.data);
        const userData = response.data;
        setIsAuthenticated(!!userData);
        if (userData) {
          setUser(userData);
          
          // Check if user needs onboarding and redirect accordingly
          const needsOnboarding = !userData.onboarding_completed_at || 
                                 !userData.tos_accepted_at || 
                                 !userData.two_factor_enabled;
          
          console.log("AuthProvider initial auth check - onboarding needed:", needsOnboarding);
          
          if (needsOnboarding && window.location.pathname === '/') {
            console.log("AuthProvider redirecting to onboarding from initial auth check");
            window.location.href = '/onboarding';
            return;
          }
        }
      } catch (error: any) {
        console.error("Authentication check error:", error.response || error);
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };
    checkAuth();
  }, []);

  const login = async (email: string, password: string): Promise<{requires2FA?: boolean, userId?: number, needsOnboarding?: boolean}> => {
    console.log("Attempting to login with email:", email);
    try {
      const response = await apiClient.post('/auth/session/', { email, password });
      console.log("Login response:", response.data);
      
      if (response.data) {
        // Check if 2FA is required
        if (response.data.requires_2fa) {
          console.log("2FA is required for this login");
          return {
            requires2FA: true,
            userId: response.data.user_id
          };
        }
        
        // Normal login flow (no 2FA)
        setIsAuthenticated(true);
        setUser(response.data);
        
        // Check if user needs onboarding
        const needsOnboarding = !response.data.onboarding_completed_at || 
                               !response.data.tos_accepted_at || 
                               !response.data.two_factor_enabled;
        
        console.log("Onboarding check:", {
          onboarding_completed_at: response.data.onboarding_completed_at,
          tos_accepted_at: response.data.tos_accepted_at,
          two_factor_enabled: response.data.two_factor_enabled,
          needsOnboarding
        });
        
        return { needsOnboarding };
      } else {
        throw new Error('Login failed');
      }
    } catch (error: any) {
      console.error("Login error:", error);
      
      if (error.response) {
        console.error("Response details:", {
          status: error.response.status,
          data: error.response.data
        });
        
        if (error.response.status === 400) {
          throw new Error('Invalid credentials');
        } else if (error.response.status === 401) {
          throw new Error('Unauthorized');
        } else if (error.response.status === 403) {
          throw new Error('Forbidden');
        } else {
          throw new Error(`Login error: ${error.response.status}`);
        }
      }
      
      throw error;
    }
  };
  
  const verify2FALogin = async (otp: string, userId: number): Promise<void> => {
    try {
      const response = await apiClient.post('/auth/verify-login/', { otp_code: otp });
      
      if (response.data) {
        setIsAuthenticated(true);
        setUser(response.data);
      } else {
        throw new Error('2FA verification failed');
      }
    } catch (error: any) {
      console.error("2FA verification error:", error);
      
      if (error.response) {
        if (error.response.status === 400) {
          throw new Error('Invalid verification code');
        } else if (error.response.status === 401) {
          throw new Error('Verification session expired');
        } else {
          throw new Error(`Verification error: ${error.response.status}`);
        }
      }
      
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      console.log("Attempting to logout...");
      await apiClient.get('/auth/logout/');
      setIsAuthenticated(false);
      setUser(null);
    } catch (error) {
      console.error('Logout failed:', error);
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  const requestPasswordReset = async (email: string): Promise<void> => {
    console.log("Password reset request for:", email);
    try {
      const response = await apiClient.post('/auth/password/reset/', { email });
      
      if (!response.data) {
        throw new Error('Password reset request failed');
      }
    } catch (error: any) {
      console.error('Password reset request failed:', error);
      throw error;
    }
  };

  const resetPassword = async (
    token: string,
    password1: string,
    password2: string
  ): Promise<void> => {
    try {
      const response = await apiClient.post('/auth/password/reset/confirm/', {
        token,
        password: password1
      });

      if (!response.data) {
        throw new Error('Password reset failed');
      }
    } catch (error: any) {
      console.error('Password reset failed:', error);
      throw error;
    }
  };

  const setup2FA = async (): Promise<{qrCodeUrl: string, secretKey: string}> => {
    try {
      console.log("Initiating 2FA setup...");
      const response = await apiClient.get('/auth/two-factor/setup/');
      console.log("2FA setup response:", response);
      
      if (!response.data || !response.data.qr_code_url || !response.data.secret_key) {
        console.error("2FA setup response missing required data:", response.data);
        throw new Error('Invalid response from 2FA setup endpoint');
      }
      
      return {
        qrCodeUrl: response.data.qr_code_url,
        secretKey: response.data.secret_key
      };
    } catch (error: any) {
      console.error("2FA setup error:", error);
      
      if (error.response) {
        console.error("Response status:", error.response.status);
        console.error("Response data:", error.response.data);
        
        // More descriptive error based on status code
        if (error.response.status === 401) {
          throw new Error('Authentication required for 2FA setup');
        } else if (error.response.status === 403) {
          throw new Error('Not authorized to set up 2FA');
        } else if (error.response.status === 500) {
          throw new Error('Server error during 2FA setup');
        }
      }
      
      throw new Error('Failed to set up 2FA');
    }
  };

  const verify2FASetup = async (otp: string): Promise<boolean> => {
    try {
      const response = await apiClient.post('/auth/two-factor/verify/', { otp_code: otp });
      
      if (response.data.success) {
        // Update the user with 2FA enabled
        setUser(response.data.user);
        return true;
      }
      return false;
    } catch (error: any) {
      console.error("2FA verification setup error:", error);
      throw new Error('Failed to verify 2FA setup');
    }
  };

  const disable2FA = async (): Promise<boolean> => {
    try {
      const response = await apiClient.post('/auth/two-factor/disable/');
      
      if (response.data.success) {
        // Update the user with 2FA disabled
        setUser(response.data.user);
        return true;
      }
      return false;
    } catch (error: any) {
      console.error("Disable 2FA error:", error);
      throw new Error('Failed to disable 2FA');
    }
  };

  const validateResetToken = async (token: string): Promise<boolean> => {
    try {
      const response = await apiClient.post('/auth/password/reset/validate_token/', { token });
      return response.data.status === 'OK';
    } catch (error: any) {
      return false;
    }
  };

  const acceptTerms = async (): Promise<void> => {
    try {
      const response = await apiClient.post('/auth/accept-terms/');
      if (response.data) {
        // Update user with terms accepted timestamp
        setUser(response.data);
      }
    } catch (error: any) {
      console.error("Accept terms error:", error);
      throw new Error('Failed to accept terms');
    }
  };

  const completeOnboarding = async (): Promise<void> => {
    try {
      const response = await apiClient.post('/auth/complete-onboarding/');
      if (response.data) {
        // Update user with completed onboarding status
        setUser(response.data);
      }
    } catch (error: any) {
      console.error("Complete onboarding error:", error);
      throw new Error('Failed to complete onboarding');
    }
  };

  const value = useMemo(
    () => ({
      isAuthenticated,
      isLoading,
      user,
      login,
      verify2FALogin,
      setup2FA,
      verify2FASetup,
      disable2FA,
      logout,
      requestPasswordReset,
      resetPassword,
      validateResetToken,
      acceptTerms,
      completeOnboarding,
    }),
    [isAuthenticated, isLoading, user]
  );

  return <AuthProviderContext.Provider value={value}>{children}</AuthProviderContext.Provider>;
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthProviderContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
